import api from "../api";
import type { User } from "../auth";

export interface ChatParticipant {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  joinedAt?: string;
  notifications?: boolean;
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: User;
  mentionsBot: boolean;
  metadata?: any;
  createdAt: string;
  editedAt?: string;
  deletedAt?: string;
}

export interface Conversation {
  id: string;
  name?: string;
  isGroup: boolean;
  participants: ChatParticipant[];
  participantCount: number;
  lastMessage?: {
    id: string;
    content: string;
    sender: User;
    createdAt: string;
  };
  unreadCount: number;
  createdAt: string;
  updatedAt: string;
}

const chatService = {
  getConversations: async (
    params: {
      page?: number;
      limit?: number;
    } = {},
  ): Promise<{ conversations: Conversation[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });

    const response = await api.get<{
      conversations: Conversation[];
      pagination: any;
    }>(`chat/conversations?${queryParams.toString()}`);
    return response.data;
  },

  getConversation: async (id: string): Promise<Conversation> => {
    const response = await api.get<Conversation>(`chat/conversations/${id}`);
    return response.data;
  },

  createConversation: async (data: {
    participantIds: string[];
    name?: string;
    isGroup?: boolean;
  }): Promise<Conversation> => {
    const response = await api.post<Conversation>("chat/conversations", data);
    return response.data;
  },

  getMessages: async (
    conversationId: string,
    params: {
      page?: number;
      limit?: number;
      before?: string;
    } = {},
  ): Promise<{ messages: ChatMessage[]; pagination: any }> => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) queryParams.append(key, value.toString());
    });

    const response = await api.get<{
      messages: ChatMessage[];
      pagination: any;
    }>(
      `chat/conversations/${conversationId}/messages?${queryParams.toString()}`,
    );
    return response.data;
  },

  sendMessage: async (
    conversationId: string,
    data: {
      content: string;
      mentionsBot?: boolean;
    },
  ): Promise<ChatMessage> => {
    const response = await api.post<ChatMessage>(
      `chat/conversations/${conversationId}/messages`,
      data,
    );
    return response.data;
  },

  markRead: async (conversationId: string): Promise<void> => {
    await api.put(`chat/conversations/${conversationId}/read`);
  },

  leaveConversation: async (conversationId: string): Promise<void> => {
    await api.post(`chat/conversations/${conversationId}/leave`);
  },

  deleteMessage: async (
    conversationId: string,
    messageId: string,
  ): Promise<void> => {
    await api.delete(
      `chat/conversations/${conversationId}/messages/${messageId}`,
    );
  },
};

export default chatService;
export const chatApi = chatService;
